import React from 'react';
import {
  alpha,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Link as MuiLink,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import { Link as RouterLink } from 'react-router-dom';
import type { PageHelpContent } from '../../config/helpTypes';
import { PAGE_HELP_DRAWER_WIDTH_PX } from './pageHelpDrawerWidth';

export type PageHelpDrawerProps = {
  open: boolean;
  onClose: () => void;
  /** Título de la pantalla actual (menú / ruta). */
  pageTitle: string;
  content: PageHelpContent;
  /** Si hay tour guiado para esta ruta, muestra el CTA. */
  tourAvailable?: boolean;
  onStartTour?: () => void;
};

const drawerPaperSx = {
  width: { xs: '100%', sm: PAGE_HELP_DRAWER_WIDTH_PX.sm, md: PAGE_HELP_DRAWER_WIDTH_PX.md },
  maxWidth: '100vw',
  p: 0,
  borderTopLeftRadius: { xs: 0, sm: 16 },
  borderBottomLeftRadius: { xs: 0, sm: 16 },
  overflow: 'hidden',
  boxShadow: (theme: import('@mui/material/styles').Theme) =>
    theme.palette.mode === 'dark'
      ? '0 0 40px rgba(0,0,0,0.45)'
      : '0 12px 40px rgba(15, 23, 42, 0.12)',
};

const PageHelpDrawer: React.FC<PageHelpDrawerProps> = ({
  open,
  onClose,
  pageTitle,
  content,
  tourAvailable = false,
  onStartTour,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const showGuideSubtitle =
    content.title.trim().toLowerCase() !== pageTitle.trim().toLowerCase() &&
    !content.title.toLowerCase().includes(pageTitle.toLowerCase());

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      /* AppBar usa zIndex.drawer+1; el drawer temporal debe quedar encima para no tapar el encabezado del panel. */
      sx={{ zIndex: (t) => t.zIndex.modal }}
      PaperProps={{
        sx: drawerPaperSx,
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': 'page-help-drawer-page-title',
        'aria-describedby': 'page-help-drawer-desc',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          px: 2.5,
          pt: 2.25,
          pb: 2.5,
          background: (t) =>
            `linear-gradient(145deg, ${alpha(t.palette.primary.main, 0.14)} 0%, ${alpha(
              t.palette.primary.light,
              0.06
            )} 42%, ${alpha(t.palette.background.paper, 1)} 100%)`,
          borderBottom: 1,
          borderColor: 'divider',
          '&::after': {
            content: '""',
            position: 'absolute',
            right: -40,
            top: -40,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: (t) => alpha(t.palette.primary.main, 0.08),
            pointerEvents: 'none',
          },
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ position: 'relative', zIndex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
            <Chip
              icon={<MenuBookOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
              label="Ayuda"
              size="small"
              color="primary"
              variant="filled"
              sx={{
                fontWeight: 600,
                letterSpacing: 0.02,
                boxShadow: (t) => `0 1px 4px ${alpha(t.palette.primary.main, 0.25)}`,
              }}
            />
          </Stack>
          <IconButton
            onClick={onClose}
            aria-label="Cerrar ayuda"
            size="small"
            sx={{
              mt: -0.5,
              bgcolor: (t) => alpha(t.palette.common.black, 0.04),
              '&:hover': { bgcolor: (t) => alpha(t.palette.common.black, 0.08) },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Typography
          id="page-help-drawer-page-title"
          component="h2"
          variant={isXs ? 'h6' : 'h5'}
          sx={{
            mt: 2,
            fontWeight: 700,
            lineHeight: 1.25,
            letterSpacing: -0.02,
            color: 'text.primary',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {pageTitle}
        </Typography>

        {showGuideSubtitle && (
          <Typography
            id="page-help-drawer-desc"
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.75, position: 'relative', zIndex: 1 }}
          >
            {content.title}
          </Typography>
        )}
        {!showGuideSubtitle && (
          <Box
            id="page-help-drawer-desc"
            component="span"
            sx={{
              position: 'absolute',
              width: 1,
              height: 1,
              p: 0,
              m: -1,
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            Guía contextual: {content.title}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          overflow: 'auto',
          px: 2.5,
          py: 2.5,
          pb: 4,
          bgcolor: (t) => (t.palette.mode === 'dark' ? t.palette.background.default : alpha(t.palette.grey[50], 1)),
        }}
      >
        {tourAvailable && onStartTour && (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<ExploreOutlinedIcon />}
            onClick={() => {
              onClose();
              // Dejar cerrar el drawer antes de abrir el tour
              window.setTimeout(() => onStartTour(), 180);
            }}
            sx={{
              mb: 2.5,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              py: 1.1,
              boxShadow: (t) => `0 2px 8px ${alpha(t.palette.primary.main, 0.28)}`,
            }}
          >
            Tour guiado
          </Button>
        )}

        {content.intro && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2.5,
              lineHeight: 1.65,
              p: 1.75,
              borderRadius: 2,
              bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.08 : 0.06),
              border: 1,
              borderColor: (t) => alpha(t.palette.primary.main, 0.12),
            }}
          >
            {content.intro}
          </Typography>
        )}

        {content.sections.map((section) => (
          <Accordion
            key={section.id}
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{
              '&:before': { display: 'none' },
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              mb: 1.25,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              transition: 'border-color 0.2s, box-shadow 0.2s',
              '&:hover': {
                borderColor: (t) => alpha(t.palette.primary.main, 0.35),
                boxShadow: (t) => `0 2px 12px ${alpha(t.palette.common.black, 0.06)}`,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
              aria-controls={`${section.id}-content`}
              id={`${section.id}-header`}
              sx={{
                minHeight: 48,
                px: 1.5,
                '& .MuiAccordionSummary-content': { my: 1 },
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                {section.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails id={`${section.id}-content`} sx={{ pt: 0, px: 1.5, pb: 2 }}>
              {section.body && (
                <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: section.bullets?.length ? 1.25 : 0, lineHeight: 1.65 }}>
                  {section.body}
                </Typography>
              )}
              {section.bullets && section.bullets.length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 2.25, listStyleType: 'disc' }}>
                  {section.bullets.map((b, i) => (
                    <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.75, lineHeight: 1.6 }}>
                      {b}
                    </Typography>
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}

        {content.links && content.links.length > 0 && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Typography variant="subtitle2" fontWeight={700} gutterBottom color="text.primary">
              Enlaces útiles
            </Typography>
            <Stack spacing={0.75}>
              {content.links.map((l) => (
                <MuiLink
                  key={l.to}
                  component={RouterLink}
                  to={l.to}
                  onClick={onClose}
                  underline="hover"
                  variant="body2"
                  fontWeight={500}
                  sx={{
                    py: 0.75,
                    px: 1.25,
                    borderRadius: 1.5,
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                    color: 'primary.main',
                    '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.12) },
                  }}
                >
                  {l.label}
                </MuiLink>
              ))}
            </Stack>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default PageHelpDrawer;
